import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { FileText, Printer, Download } from 'lucide-react';
import { Button } from '../../components/ui';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

// Exercise types and their display colors
const EXERCISE_TYPES = [
  { key: 'quad', label: 'Quad Test', color: '#60A5FA' },        // blue
  { key: 'hamstring', label: 'Hamstring Test', color: '#4ADE80' }, // green
  { key: 'singleLegHop', label: 'Single Leg Hop', color: '#F87171' }, // red
  { key: 'rotationalHop', label: 'Rotational Hop', color: '#FACC15' }, // yellow
  { key: 'tripleMedialHop', label: 'Triple Medial Hop', color: '#A78BFA' }, // purple
];

// Which exercises appear in which phase
const PHASE_EXERCISES = {
  1: ['quad', 'hamstring'],
  2: ['quad', 'hamstring', 'singleLegHop', 'sidePlank'],
  3: ['quad', 'hamstring', 'singleLegHop', 'rotationalHop', 'sidePlank'],
  4: ['quad', 'hamstring', 'singleLegHop', 'rotationalHop', 'tripleMedialHop', 'sidePlank'],
};

// Side Plank Visual Component
function SidePlankCriteria() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">Side Plank Criteria</h3>
      </div>
      <div className="p-4">
        <img 
          src="/images/sideplank-criteria.png" 
          alt="Side Plank Criteria by Phase" 
          className="w-full h-auto"
        />
      </div>
    </div>
  );
}

// Simple Side Plank Icon SVG
function SidePlankIcon({ variant = 0 }) {
  // Different variants for different phases
  return (
    <svg width="80" height="40" viewBox="0 0 80 40" className="text-gray-700">
      {/* Body base - horizontal plank position */}
      <ellipse cx="20" cy="20" rx="8" ry="6" fill="currentColor" /> {/* Head */}
      <rect x="26" y="16" width="30" height="8" rx="4" fill="currentColor" /> {/* Torso */}
      
      {/* Supporting arm */}
      <line x1="30" y1="24" x2="30" y2="36" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      
      {/* Bottom leg */}
      <rect x="54" y="18" width="20" height="4" rx="2" fill="currentColor" />
      
      {/* Top leg - raised in variants 1 and 2 */}
      {variant === 0 ? (
        <rect x="54" y="14" width="20" height="4" rx="2" fill="currentColor" />
      ) : (
        <rect x="54" y="6" width="20" height="4" rx="2" fill="currentColor" className="opacity-80" />
      )}
    </svg>
  );
}

// Footprint Icon SVG
function FootprintIcon({ filled = false }) {
  return (
    <svg width="24" height="32" viewBox="0 0 24 32" className={filled ? 'text-gray-800' : 'text-gray-400'}>
      {/* Heel */}
      <ellipse cx="12" cy="26" rx="6" ry="5" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" />
      {/* Ball of foot */}
      <ellipse cx="12" cy="12" rx="8" ry="6" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" />
      {/* Toes */}
      <circle cx="6" cy="4" r="2" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1" />
      <circle cx="10" cy="2" r="2" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1" />
      <circle cx="14" cy="2" r="2" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1" />
      <circle cx="18" cy="4" r="2" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1" />
    </svg>
  );
}

export function TestOutReportPage() {
  const { user, aclCare } = useAuth();
  const [loading, setLoading] = useState(true);
  const [testOutData, setTestOutData] = useState({});
  const [error, setError] = useState(null);

  // Fetch test out data for all phases
  useEffect(() => {
    async function fetchTestOuts() {
      if (!user?.id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const results = {};
        
        // Fetch test outs for each phase
        for (let phase = 1; phase <= 4; phase++) {
          try {
            const data = await api.getTestOuts(user.id, { phase });
            // Filter to only passing attempts and get highest scores
            const passingAttempts = (data || []).filter(attempt => attempt.overall_passed);
            if (passingAttempts.length > 0) {
              // Get the best scores across all passing attempts
              results[phase] = getBestScores(passingAttempts);
            }
          } catch (err) {
            console.warn(`No test out data for phase ${phase}:`, err);
          }
        }
        
        setTestOutData(results);
      } catch (err) {
        console.error('Error fetching test out data:', err);
        setError('Failed to load test out data');
      } finally {
        setLoading(false);
      }
    }
    
    fetchTestOuts();
  }, [user?.id]);

  // Extract best scores from passing attempts
  const getBestScores = (passingAttempts) => {
    const bestScores = {};
    
    passingAttempts.forEach(attempt => {
      if (attempt.scores) {
        Object.entries(attempt.scores).forEach(([key, value]) => {
          if (!bestScores[key] || (value.score > bestScores[key].score)) {
            bestScores[key] = value;
          }
        });
      }
    });
    
    return bestScores;
  };

  // Build chart data
  const buildChartData = () => {
    const data = [];
    
    for (let phase = 1; phase <= 4; phase++) {
      const phaseData = { phase: `Phase ${phase}` };
      const phaseScores = testOutData[phase] || {};
      
      EXERCISE_TYPES.forEach(({ key }) => {
        if (PHASE_EXERCISES[phase].includes(key)) {
          const score = phaseScores[key]?.score;
          phaseData[key] = score != null ? Math.round(score * 100) : null;
        }
      });
      
      data.push(phaseData);
    }
    
    return data;
  };

  // Calculate age from birthday
  const calculateAge = (birthday) => {
    if (!birthday) return null;
    const birthDate = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  const chartData = buildChartData();
  const athleteName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'N/A';
  const athleteAge = calculateAge(user?.patient?.birthday);
  const surgeryDate = user?.acl_care?.surgery_date || aclCare?.surgery_date;
  const injurySide = user?.acl_care?.injury_side || aclCare?.injury_side;

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            entry.value != null && (
              <p key={index} className="text-sm" style={{ color: entry.color }}>
                {entry.name}: {entry.value}%
              </p>
            )
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="max-w-4xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Print Styles */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-area, .print-area * {
              visibility: visible;
            }
            .print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 20px;
            }
            .no-print {
              display: none !important;
            }
            .print-break {
              page-break-before: always;
            }
          }
        `}
      </style>

      {/* Header with Print Button */}
      <div className="flex items-center justify-between mb-6 no-print">
        <div className="flex items-center">
          <FileText className="h-6 w-6 text-primary mr-2" />
          <h1 className="text-2xl font-bold text-gray-900">Test Out Report</h1>
        </div>
        <Button variant="outline" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          Print Report
        </Button>
      </div>

      {/* Printable Content */}
      <div className="print-area space-y-6">
        {/* Print Header (only shows when printing) */}
        <div className="hidden print:block text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Test Out Report</h1>
        </div>

        {/* Athlete Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Athlete Name</p>
              <p className="font-semibold text-gray-900">{athleteName || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Age</p>
              <p className="font-semibold text-gray-900">{athleteAge || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Surgery Date</p>
              <p className="font-semibold text-gray-900">{formatDate(surgeryDate)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Affected Side</p>
              <p className="font-semibold text-gray-900 capitalize">{injurySide || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading test out data...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Chart Section */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Test Out Scores by Phase</h3>
            
            {Object.keys(testOutData).length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-gray-500">
                  No passing test out attempts yet. Complete your first test out to see results here.
                </p>
              </div>
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="phase" 
                      tick={{ fill: '#6B7280', fontSize: 12 }}
                      axisLine={{ stroke: '#D1D5DB' }}
                    />
                    <YAxis 
                      domain={[0, 100]}
                      tick={{ fill: '#6B7280', fontSize: 12 }}
                      axisLine={{ stroke: '#D1D5DB' }}
                      tickFormatter={(value) => `${value}`}
                      label={{ 
                        value: 'Score (%)', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { fill: '#6B7280', fontSize: 12 }
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      formatter={(value) => <span className="text-sm text-gray-700">{value}</span>}
                    />
                    {EXERCISE_TYPES.map(({ key, label, color }) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        name={label}
                        fill={color}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Passing Thresholds Reference */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Passing Thresholds by Phase</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Exercise</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-500">Phase I</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-500">Phase II</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-500">Phase III</th>
                    <th className="px-4 py-2 text-center font-medium text-gray-500">Phase IV</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">Quad Test</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥60%</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥70%</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥80%</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥90%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">Hamstring Test</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥60%</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥70%</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥80%</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥90%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">Single Leg Hop</td>
                    <td className="px-4 py-3 text-center text-gray-400">—</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥80%</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥90%</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥90%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">Rotational Hop</td>
                    <td className="px-4 py-3 text-center text-gray-400">—</td>
                    <td className="px-4 py-3 text-center text-gray-400">—</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥85%</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥90%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">Triple Medial Hop</td>
                    <td className="px-4 py-3 text-center text-gray-400">—</td>
                    <td className="px-4 py-3 text-center text-gray-400">—</td>
                    <td className="px-4 py-3 text-center text-gray-400">—</td>
                    <td className="px-4 py-3 text-center text-gray-700">≥90%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Side Plank Criteria */}
        {!loading && !error && <SidePlankCriteria />}
      </div>
    </div>
  );
}